import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';

const useResortUserData = (resortId, resortName) => {
  const { user } = useAuth();
  const [visited, setVisited] = useState(false);
  const [rating, setRatingState] = useState(null);
  const [aggregateRating, setAggregateRating] = useState(null);
  const [loading, setLoading] = useState(true);

  // Listen to aggregate rating (for all users, real-time)
  useEffect(() => {
    if (!resortId) return;
    const unsub = onSnapshot(doc(db, 'resortRatings', resortId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAggregateRating({ average: data.averageRating, count: data.ratingCount });
      } else {
        setAggregateRating(null);
      }
    });
    return () => unsub();
  }, [resortId]);

  // Fetch user's visited/rating data
  useEffect(() => {
    if (!user || !resortId) {
      setVisited(false);
      setRatingState(null);
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid, 'visitedResorts', resortId));
        if (snap.exists()) {
          setVisited(true);
          setRatingState(snap.data().rating ?? null);
        } else {
          setVisited(false);
          setRatingState(null);
        }
      } catch (error) {
        console.error('Error fetching user resort data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, resortId]);

  const toggleVisited = useCallback(async () => {
    if (!user || !resortId) return;

    const userResortRef = doc(db, 'users', user.uid, 'visitedResorts', resortId);
    const ratingsRef = doc(db, 'resortRatings', resortId);

    if (visited) {
      // Removing: if there was a rating, update the aggregate
      if (rating != null) {
        await runTransaction(db, async (transaction) => {
          const ratingsSnap = await transaction.get(ratingsRef);
          transaction.delete(userResortRef);

          if (ratingsSnap.exists()) {
            const data = ratingsSnap.data();
            const newCount = Math.max(0, data.ratingCount - 1);
            const newSum = data.ratingSum - rating;
            if (newCount === 0) {
              transaction.delete(ratingsRef);
            } else {
              transaction.update(ratingsRef, {
                ratingCount: newCount,
                ratingSum: newSum,
                averageRating: Math.round((newSum / newCount) * 10) / 10,
                updatedAt: serverTimestamp(),
              });
            }
          }
        });
      } else {
        await deleteDoc(userResortRef);
      }
      setVisited(false);
      setRatingState(null);
    } else {
      // Adding as visited
      await setDoc(userResortRef, {
        resortId,
        resortName: resortName || resortId,
        visitedAt: serverTimestamp(),
        rating: null,
        ratedAt: null,
      });
      setVisited(true);
    }
  }, [user, resortId, resortName, visited, rating]);

  const setRating = useCallback(async (newRating) => {
    if (!user || !resortId || !visited) return;

    const userResortRef = doc(db, 'users', user.uid, 'visitedResorts', resortId);
    const ratingsRef = doc(db, 'resortRatings', resortId);
    const oldRating = rating;

    await runTransaction(db, async (transaction) => {
      const ratingsSnap = await transaction.get(ratingsRef);

      // Update user's rating
      transaction.update(userResortRef, {
        rating: newRating,
        ratedAt: serverTimestamp(),
      });

      // Update aggregate
      if (ratingsSnap.exists()) {
        const data = ratingsSnap.data();
        let newSum, newCount;

        if (oldRating != null) {
          // Changing existing rating
          newSum = data.ratingSum - oldRating + newRating;
          newCount = data.ratingCount;
        } else {
          // New rating
          newSum = data.ratingSum + newRating;
          newCount = data.ratingCount + 1;
        }

        transaction.update(ratingsRef, {
          ratingSum: newSum,
          ratingCount: newCount,
          averageRating: Math.round((newSum / newCount) * 10) / 10,
          updatedAt: serverTimestamp(),
        });
      } else {
        // First rating for this resort
        transaction.set(ratingsRef, {
          resortId,
          ratingSum: newRating,
          ratingCount: 1,
          averageRating: newRating,
          updatedAt: serverTimestamp(),
        });
      }
    });

    setRatingState(newRating);
  }, [user, resortId, visited, rating]);

  const removeRating = useCallback(async () => {
    if (!user || !resortId || rating == null) return;

    const userResortRef = doc(db, 'users', user.uid, 'visitedResorts', resortId);
    const ratingsRef = doc(db, 'resortRatings', resortId);

    await runTransaction(db, async (transaction) => {
      const ratingsSnap = await transaction.get(ratingsRef);

      transaction.update(userResortRef, {
        rating: null,
        ratedAt: null,
      });

      if (ratingsSnap.exists()) {
        const data = ratingsSnap.data();
        const newCount = Math.max(0, data.ratingCount - 1);
        const newSum = data.ratingSum - rating;
        if (newCount === 0) {
          transaction.delete(ratingsRef);
        } else {
          transaction.update(ratingsRef, {
            ratingCount: newCount,
            ratingSum: newSum,
            averageRating: Math.round((newSum / newCount) * 10) / 10,
            updatedAt: serverTimestamp(),
          });
        }
      }
    });

    setRatingState(null);
  }, [user, resortId, rating]);

  return { visited, rating, aggregateRating, loading, toggleVisited, setRating, removeRating };
};

export default useResortUserData;
